import { effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService, ToastService, ToastType, WINDOW } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, switchMap } from 'rxjs';

import { clearPersistedProfileState } from '@zeusm/shared/session/profile/helpers';

import { AUTH_API } from './api';
import { AuthSyncService } from './auth-sync.service';
import { AuthState } from './models';
import {
  RouterStore,
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '../../store';

const initialState: AuthState = {
  isAuthorized: false,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withDevtools('auth'),
  withState(initialState),
  withRequestStatus(['logout']),
  withRequestHandler(),
  withProps(store => ({
    store,
    authApi: inject(AUTH_API),
    routerStore: inject(RouterStore),
    toast: inject(ToastService),
    window: inject<Window>(WINDOW),
    spinnerService: inject(SpinnerService),
    storeHelperService: inject(StoreHelperService),
    authSyncService: inject(AuthSyncService),
  })),
  withMethods(({ store }) => ({
    setAuthStatus: (state: boolean) => {
      patchState(store, { isAuthorized: state });
    },
  })),
  withMethods(({ window, routerStore, setAuthStatus }) => ({
    clearAuthState: () => {
      const { pathname } = window.location;
      const path = pathname.includes('signin') ? '' : pathname;

      clearPersistedProfileState(window.localStorage);

      const route = ['/signin'];
      const extras = {
        queryParams: {
          url: !path || path === '/' ? null : encodeURIComponent(path),
        },
      };
      routerStore.navigate(route, extras);
      setAuthStatus(false);
    },
  })),
  withMethods(({ store, authApi, toast, clearAuthState, authSyncService }) => ({
    logout: rxMethod<void>(
      switchMap(() => {
        if (store.isLogoutPending()) {
          return EMPTY;
        }

        patchState(store, setPendingStatusFor('logout'));
        return authApi.logout().pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('logout'));
              authSyncService.notifyLogout();
              clearAuthState();
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('logout', error));
              toast.show({ type: ToastType.Error, message: error.message });
            },
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
